/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Modal, Field, SignalBars, ConfirmModal } from "../components/common";
import ThemeEditor from "../components/gm/ThemeEditor";
import { Plus, LogOut, ChevronRight, Archive, Globe, Users, Radio, MessageSquare, Palette, Copy, Pencil, Trash2 } from "lucide-react";

export default function GMDashboard() {
  const nav = useNavigate();
  const { logout, user } = useAuth();
  const [universes, setUniverses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [showUni, setShowUni] = useState(false);
  const [showCamp, setShowCamp] = useState(false);
  const [showTheme, setShowTheme] = useState(false);
  const [uniToDelete, setUniToDelete] = useState(null);
  const [campToDelete, setCampToDelete] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [uniName, setUniName] = useState("");
  const [campName, setCampName] = useState("");

  async function loadUniverses() {
    const r = await api.get("/universes");
    setUniverses(r.data);
  }
  async function openUniverse(u) {
    setSelected(u);
    const r = await api.get(`/universes/${u.id}/campaigns`);
    setCampaigns(r.data);
  }
  useEffect(() => { loadUniverses(); }, []);

  async function createUniverse() {
    if (!uniName.trim()) return;
    await api.post("/universes", { name: uniName.trim() });
    setUniName(""); setShowUni(false); loadUniverses();
  }
  async function renameUniverse(u) {
    setRenameTarget({ kind: "universe", item: u });
    setRenameValue(u.name);
  }
  async function deleteUniverse(u) {
    setUniToDelete(u);
  }
  async function confirmDeleteUniverse() {
    await api.delete(`/universes/${uniToDelete.id}`);
    setUniToDelete(null);
    loadUniverses();
  }
  async function createCampaign() {
    if (!campName.trim() || !selected) return;
    const r = await api.post("/campaigns", { universe_id: selected.id, name: campName.trim() });
    setCampName(""); setShowCamp(false);
    openUniverse(selected);
    nav(`/gm/campaign/${r.data.id}`);
  }
  async function toggleArchive(c) {
    await api.patch(`/campaigns/${c.id}`, { status: c.status === "active" ? "archived" : "active" });
    openUniverse(selected);
  }
  async function duplicate(c) {
    await api.post(`/campaigns/${c.id}/duplicate`);
    openUniverse(selected);
  }
  async function renameCampaign(c) {
    setRenameTarget({ kind: "campaign", item: c });
    setRenameValue(c.name);
  }
  async function confirmRename() {
    const n = renameValue.trim();
    if (!n) return;
    if (renameTarget.kind === "universe") {
      await api.patch(`/universes/${renameTarget.item.id}`, { name: n });
      loadUniverses();
    } else {
      await api.patch(`/campaigns/${renameTarget.item.id}`, { name: n });
      openUniverse(selected);
    }
    setRenameTarget(null);
  }
  async function confirmDeleteCampaign() {
    await api.delete(`/campaigns/${campToDelete.id}`);
    setCampToDelete(null);
    openUniverse(selected);
  }

  return (
    <div className="min-h-full">
      <header className="h-14 px-5 flex items-center justify-between border-b border-[color:var(--r-primary)]/15 panel sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <SignalBars />
          <span className="font-title font-black tracking-tight text-lg">RÉPLA <span style={{ color: "var(--r-primary)" }}>// COM</span></span>
          <span className="sys-label hidden sm:inline">CONSOLE MAÎTRE DU JEU</span>
        </div>
        <div className="flex items-center gap-3">
          {user?.login_id && <span data-testid="gm-repla-id" className="font-mono text-[10px] px-2 py-1 hidden sm:inline" title="Votre ID de connexion RÉPLA" style={{ color: "var(--r-primary)", border: "1px solid color-mix(in srgb, var(--r-primary) 30%, transparent)" }}>MON ID · {user.login_id}</span>}
          <button data-testid="logout-button" className="btn btn-ghost flex items-center gap-2" onClick={() => { logout(); nav("/login"); }}>
            <LogOut size={14} /> DÉCONNEXION
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        {!selected ? (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="sys-label">RÉPERTOIRE</div>
                <h2 className="font-title font-bold text-2xl tracking-tight mt-1">MES UNIVERS</h2>
              </div>
              <button data-testid="create-universe-button" className="btn btn-solid flex items-center gap-2" onClick={() => setShowUni(true)}>
                <Plus size={14} /> CRÉER UN UNIVERS
              </button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {universes.map((u) => (
                <div key={u.id} data-testid={`universe-card-${u.id}`} className="frame p-5 group">
                  <button onClick={() => openUniverse(u)} className="text-left w-full">
                    <Globe size={18} style={{ color: "var(--r-primary)" }} />
                    <div className="font-title font-bold text-lg mt-3 tracking-tight break-words leading-tight">{u.name}</div>
                    <div className="sys-label mt-2 flex items-center gap-2">
                      {u.campaign_count} CAMPAGNE{u.campaign_count > 1 ? "S" : ""}
                      <ChevronRight size={12} />
                    </div>
                  </button>
                  <div className="flex gap-2 mt-4 pt-3 border-t border-[color:var(--r-primary)]/10">
                    <button data-testid={`rename-universe-${u.id}`} onClick={() => renameUniverse(u)} className="btn btn-ghost flex-1 justify-center flex items-center gap-1 text-[10px]" style={{ padding: "6px 8px" }}><Pencil size={11} /> RENOMMER</button>
                    <button data-testid={`delete-universe-${u.id}`} onClick={() => deleteUniverse(u)} className="btn btn-ghost flex-1 justify-center flex items-center gap-1 text-[10px]" style={{ padding: "6px 8px", color: "var(--r-alert)" }}><Trash2 size={11} /> SUPPRIMER</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <button className="sys-label flex items-center gap-1 mb-4 hover:text-[color:var(--r-primary)]" onClick={() => setSelected(null)}>
              ← MES UNIVERS
            </button>
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="sys-label">{selected.name}</div>
                <h2 className="font-title font-bold text-2xl tracking-tight mt-1">CAMPAGNES</h2>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="create-campaign-button" className="btn btn-solid flex items-center gap-2" onClick={() => setShowCamp(true)}>
                  <Plus size={14} /> CRÉER UNE PARTIE
                </button>
                <button data-testid="universe-theme-button" className="btn btn-ghost flex items-center gap-2" onClick={() => setShowTheme(true)}>
                  <Palette size={14} /> THÈME DE L'UNIVERS
                </button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {campaigns.map((c) => (
                <div key={c.id} className="frame p-5" style={{ opacity: c.status === "archived" ? 0.55 : 1 }}>
                  <div className="flex items-start justify-between">
                    <div
                      className="cursor-pointer flex-1"
                      data-testid={`campaign-card-${c.id}`}
                      onClick={() => nav(`/gm/campaign/${c.id}`)}
                    >
                      <div className="font-title font-bold text-lg tracking-tight">{c.name}</div>
                      <div className="flex items-center gap-4 mt-3 sys-label">
                        <span className="flex items-center gap-1"><Users size={11} /> {c.players}</span>
                        <span className="flex items-center gap-1"><Radio size={11} /> {c.npcs} PNJ</span>
                        <span className="flex items-center gap-1"><MessageSquare size={11} /> {c.conversations}</span>
                      </div>
                    </div>
                    <span
                      className="font-mono text-[9px] tracking-[0.2em] px-2 py-0.5"
                      style={{
                        color: c.status === "active" ? "var(--r-primary)" : "var(--r-text-muted)",
                        border: `1px solid ${c.status === "active" ? "var(--r-primary)" : "var(--r-text-muted)"}55`,
                      }}
                    >
                      {c.status === "active" ? "ACTIVE" : "ARCHIVÉE"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-[color:var(--r-primary)]/10 flex-wrap gap-2">
                    <button className="btn btn-ghost text-[10px]" onClick={() => nav(`/gm/campaign/${c.id}`)}>OUVRIR</button>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button data-testid={`rename-campaign-${c.id}`} className="btn btn-ghost text-[10px] flex items-center gap-1" onClick={() => renameCampaign(c)}>
                        <Pencil size={11} /> RENOMMER
                      </button>
                      <button data-testid={`duplicate-campaign-${c.id}`} className="btn btn-ghost text-[10px] flex items-center gap-1" onClick={() => duplicate(c)}>
                        <Copy size={11} /> DUPLIQUER
                      </button>
                      <button data-testid={`archive-campaign-${c.id}`} className="btn btn-ghost text-[10px] flex items-center gap-1" onClick={() => toggleArchive(c)}>
                        <Archive size={11} /> {c.status === "active" ? "ARCHIVER" : "RESTAURER"}
                      </button>
                      <button data-testid={`delete-campaign-${c.id}`} className="btn btn-ghost text-[10px] flex items-center gap-1" style={{ color: "var(--r-alert)" }} onClick={() => setCampToDelete(c)}>
                        <Trash2 size={11} /> SUPPRIMER
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {campaigns.length === 0 && <div className="sys-label col-span-2">AUCUNE CAMPAGNE.</div>}
            </div>
          </>
        )}
      </main>

      <Modal open={showUni} onClose={() => setShowUni(false)} title="NOUVEL UNIVERS">
        <Field label="NOM DE L'UNIVERS">
          <input data-testid="universe-name-input" className="input" value={uniName} onChange={(e) => setUniName(e.target.value)} placeholder="TIGRES VOLANTS" autoFocus />
        </Field>
        <button data-testid="universe-create-submit" className="btn btn-solid w-full" onClick={createUniverse}>CRÉER</button>
      </Modal>

      <Modal open={showCamp} onClose={() => setShowCamp(false)} title="NOUVELLE PARTIE">
        <Field label="NOM DE LA CAMPAGNE">
          <input data-testid="campaign-name-input" className="input" value={campName} onChange={(e) => setCampName(e.target.value)} placeholder="LES ÎLES NOIRES" autoFocus />
        </Field>
        <button data-testid="campaign-create-submit" className="btn btn-solid w-full" onClick={createCampaign}>CRÉER ET OUVRIR</button>
      </Modal>

      {selected && (
        <ThemeEditor
          open={showTheme}
          onClose={() => setShowTheme(false)}
          title="THÈME DE L'UNIVERS"
          baseTheme={selected.theme}
          baseNetwork={selected.theme?.network_name}
          baseTerminology={selected.terminology}
          onSave={async ({ theme, network, terminology }) => {
            await api.patch(`/universes/${selected.id}`, { theme: { ...theme, network_name: network }, terminology });
            const r = await api.get(`/universes/${selected.id}`);
            setSelected((s) => ({ ...s, theme: r.data.theme, terminology: r.data.terminology }));
            loadUniverses();
          }}
        />
      )}

      <ConfirmModal
        open={!!uniToDelete}
        onClose={() => setUniToDelete(null)}
        onConfirm={confirmDeleteUniverse}
        title="SUPPRIMER L'UNIVERS"
        confirmLabel="SUPPRIMER"
        message={uniToDelete ? `Supprimer l'univers « ${uniToDelete.name} » et TOUTES ses campagnes, PNJ, personnages, conversations et messages ? Cette action est irréversible.` : ""}
      />

      <ConfirmModal
        open={!!campToDelete}
        onClose={() => setCampToDelete(null)}
        onConfirm={confirmDeleteCampaign}
        title="SUPPRIMER LA CAMPAGNE"
        confirmLabel="SUPPRIMER"
        message={campToDelete ? `Supprimer la campagne « ${campToDelete.name} » et tous ses PNJ, personnages, conversations et messages ? Cette action est irréversible.` : ""}
      />

      <Modal open={!!renameTarget} onClose={() => setRenameTarget(null)} title={renameTarget?.kind === "universe" ? "RENOMMER L'UNIVERS" : "RENOMMER LA CAMPAGNE"}>
        <Field label="NOUVEAU NOM">
          <input data-testid="rename-input" className="input" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus onKeyDown={(e) => { if (e.key === "Enter") confirmRename(); }} />
        </Field>
        <button data-testid="rename-submit" className="btn btn-solid w-full mt-2" onClick={confirmRename}>ENREGISTRER</button>
      </Modal>
    </div>
  );
}
